import { Link } from "react-router-dom";
import { Heart, Bed, Bath, Maximize, MapPin } from "lucide-react";
import { Card } from "@/propertyai/components/ui/card";
import { Badge } from "@/propertyai/components/ui/badge";
import { Button } from "@/propertyai/components/ui/button";
import { useState } from "react";

export const PropertyCard = ({ property, compact = false }) => {
  const [imgIdx, setImgIdx] = useState(0);
  const [saved, setSaved] = useState(false);
  const photos = property.photos || [];
  const photo = photos[imgIdx] || 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800';

  const formatPrice = (price) => {
    if (price >= 1000000) return `$${(price / 1000000).toFixed(1)}M`;
    return `$${(price / 1000).toFixed(0)}K`;
  };

  const statusColors = {
    active: 'bg-green-500/90 text-white',
    pending: 'bg-amber-500/90 text-white',
    sold: 'bg-red-500/90 text-white',
  };

  return (
    <Card
      className="group rounded-2xl border border-border bg-card shadow-[0_10px_30px_-20px_hsl(var(--shadow-color)/0.15)] hover:shadow-[0_18px_45px_-28px_hsl(var(--shadow-color)/0.3)] transition-shadow duration-200 overflow-hidden"
      data-testid="listing-card"
    >
      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={photo}
          alt={property.address}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        
        {/* Status badge */}
        <Badge className={`absolute top-3 left-3 ${statusColors[property.status] || 'bg-gray-500 text-white'} text-xs font-medium`}>
          {property.status?.charAt(0).toUpperCase() + property.status?.slice(1)}
        </Badge>

        {/* Price */}
        <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg px-2.5 py-1 shadow-sm">
          <span className="text-lg font-semibold tabular-nums font-['Space_Grotesk'] text-[#000]"> 
            ${property.price?.toLocaleString()}
          </span>
        </div>

        {/* Save button */}
        <Button
          variant="ghost"
          size="sm"
          className="absolute top-3 right-3 h-8 w-8 p-0 bg-white/90 hover:bg-white rounded-full shadow-sm text-[#000]"
        >
          <Heart className={`w-4 h-4 ${saved ? 'fill-red-500 text-red-500' : 'text-gray-500'}`} />
        </Button>

        {/* Photo indicators */}
        {photos.length > 1 && (
          <div className="absolute bottom-3 right-3 flex gap-1">
            {photos.slice(0, 5).map((_, i) => (
              <button
                key={i}
                className={`w-1.5 h-1.5 rounded-full ${i === imgIdx ? 'bg-white' : 'bg-white/50'}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setImgIdx(i); }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <Link to={`/property-ai/homes/${property.id}`} data-testid="listing-view-details-button">
        <div className="p-3.5">
          <div className="flex items-start justify-between mb-1.5">
            <div className="flex items-center gap-1 text-xs text-muted-foreground truncate">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{property.address}, {property.city}, {property.state}</span>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-3 text-sm text-foreground">
            <span className="flex items-center gap-1">
              <Bed className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="font-medium">{property.beds}</span>
              <span className="text-muted-foreground text-xs">bd</span>
            </span>
            <span className="flex items-center gap-1">
              <Bath className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="font-medium">{property.baths}</span>
              <span className="text-muted-foreground text-xs">ba</span>
            </span>
            <span className="flex items-center gap-1">
              <Maximize className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="font-medium">{property.sqft?.toLocaleString()}</span>
              <span className="text-muted-foreground text-xs">sqft</span>
            </span>
          </div>

          {!compact && property.neighborhood && (
            <div className="mt-2 flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">{property.neighborhood}</Badge>
              {property.days_on_market && (
                <span className="text-xs text-muted-foreground">{property.days_on_market}d on market</span>
              )}
            </div>
          )}
        </div>
      </Link>
    </Card>
  );
};

export default PropertyCard;

